import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Video } from '@/types/video';

export default function HeroCarousel({ items }: { items: Video[] }) {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((c) => (c + 1) % items.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [items.length]);

  const go = (dir: number) => {
    setCurrent((c) => (c + dir + items.length) % items.length);
  };

  const active = items[current];

  return (
    <section className="relative w-full overflow-hidden">
      <div className="relative h-[440px] w-full md:h-[580px]">
        {items.map((v, i) => (
          <div
            key={v.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === current ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={v.cover}
              alt={`${v.title} 焦点图`}
              title={`${v.title} - 涩涩视频热门推荐`}
              className="h-full w-full object-cover object-top"
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/45 to-black/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

        <div className="absolute inset-0">
          <div className="mx-auto flex h-full w-full flex-col justify-center px-4 md:px-8">
            <div key={active.id} className="max-w-2xl animate-fade-up">
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-accent-500 px-3 py-1 text-xs font-medium text-background-50">
                  热门推荐
                </span>
                <span className="rounded-full bg-white/15 px-3 py-1 text-xs font-medium text-background-50 backdrop-blur">
                  {active.categoryName}
                </span>
                <span className="rounded-full bg-white/15 px-3 py-1 text-xs font-medium text-background-50 backdrop-blur">
                  {active.genre}
                </span>
              </div>
              <h2 className="font-heading text-3xl font-extrabold leading-tight text-background-50 md:text-5xl">
                {active.title}
              </h2>
              <p className="mt-4 line-clamp-2 max-w-xl text-sm leading-relaxed text-background-100/90 md:text-base">
                {active.description}
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link
                  to={`/detail/${active.id}`}
                  className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-play-fill text-lg" />
                  查看详情
                </Link>
                <Link
                  to={`/category/${active.category}`}
                  className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-white/40 bg-white/10 px-5 py-2.5 text-sm font-medium text-background-50 backdrop-blur transition-colors hover:bg-white/20"
                >
                  更多{active.categoryName}
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
              <div className="mt-6 flex items-center gap-4 text-xs text-background-100/80">
                <span className="flex items-center gap-1">
                  <i className="ri-time-line" />
                  {active.duration}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-map-pin-line" />
                  {active.region}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line" />
                  {active.uploadDate}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Arrows */}
        <button
          type="button"
          aria-label="上一个"
          onClick={() => go(-1)}
          className="absolute left-3 top-1/2 flex h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black/40 text-background-50 backdrop-blur transition-colors hover:bg-primary-500 md:left-6 md:h-11 md:w-11"
        >
          <i className="ri-arrow-left-s-line text-2xl" />
        </button>
        <button
          type="button"
          aria-label="下一个"
          onClick={() => go(1)}
          className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black/40 text-background-50 backdrop-blur transition-colors hover:bg-primary-500 md:right-6 md:h-11 md:w-11"
        >
          <i className="ri-arrow-right-s-line text-2xl" />
        </button>

        {/* Dots */}
        <div className="absolute bottom-5 left-1/2 flex -translate-x-1/2 items-center gap-2">
          {items.map((v, i) => (
            <button
              key={v.id}
              type="button"
              aria-label={`切换到第 ${i + 1} 张`}
              onClick={() => setCurrent(i)}
              className={`h-1.5 cursor-pointer rounded-full transition-all ${
                i === current ? 'w-7 bg-primary-500' : 'w-2.5 bg-white/60 hover:bg-white'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}